import { useEffect, useCallback } from "react";

import ReconnectingWebSocket from "reconnecting-websocket";

// Use this hook in context providers, to maintain centralized control for many WS connections needed.
// - for each defined ref in any context for the purpose of maintaining a WS connection, we can have a unique WS connection across the app.
// - connection (with auto reconnection) is maintained when url is available and there is no active connection. meaning:
//     - to connect in a certain time, when we are ready, make sure url is available only on that time, and is empty before that.
//     - to disconnect in a certain time, make sure url becomes empty again on that time.
// - readyState of the underlying ws object is state that is not managed by react, but we also should not manage it, since if we do, we have to store it in
//   a context (near its ref), and because of auto re-connections, it'll cause too many re-rendering of the whole app. also a good ux would not rely on representing
//   a flickering underlying connection state. so we should not use readyState as a state in react. and for sending messages, the reconnecting ws lib, handles that
//   by queueing messages when connection is not ready, and sending them when it is ready.
export default function useWS(wsRef, url, { parseJson = false, onMessage = () => {} } = {}) {
  useEffect(() => {
    if (!url && wsRef.current) {
      // we were connected initially but now we don't want to be, since an empty url means we should not be connected
      console.log("WS: (no-longer needed) close connection", url, wsRef.current);
      wsRef.current.close();
      wsRef.current = null;
      return;
    }

    if (!url || wsRef.current) return; // not ready to connect yet, or already connected

    const ws = new ReconnectingWebSocket(url);
    wsRef.current = ws;
    console.log("WS: connecting to", url, ws);

    ws.onmessage = (event) => {
      // TODO: if (parseJson) JSON.parse(event.data || "{}")
      console.log("WS: onmessage", event);
      onMessage(event);
    };
    ws.onopen = (event) => {
      console.log("WS: onopen", event);
    };
    ws.onerror = (event) => {
      console.log("WS: onerror", event);
    };
  }, [url]);

  useEffect(() => {
    if (wsRef.current) {
      wsRef.current.onmessage = (event) => {
        // TODO: if (parseJson) JSON.parse(event.data || "{}")
        console.log("WS: onmessage", event);
        onMessage(event);
      };
    }
  }, [onMessage]);

  useEffect(
    () => () => {
      const ws = wsRef.current;
      console.log("WS: (utilizer component unmount) close connection", url, ws);
      if (ws) {
        ws.close();
        wsRef.current = null;
      }
    },
    [],
  );

  const send = useCallback((message) => {
    if (wsRef.current) {
      // TODO: make sure message queueing and sending also works before connection is ready for the first time (not just while reconnecting)
      wsRef.current.send(message);
    }
  });

  const sendJson = useCallback((data) => {
    // TODO: make sure message queueing and sending also works before connection is ready for the first time (not just while reconnecting)
    send(JSON.stringify(data));
  });

  return { send, sendJson };
}
